import { Grid } from '@mui/material';
import {
  PaypalMetadataEntity,
  PlatformLinkingProvider,
  PlatformLinkingProviderEntity,
  SocketClientMessage,
} from '@okletsplay/core';
import { useSocketSubscriber } from 'app/hooks/socket';
import { useFetchData } from 'app/hooks/store';
import { getUserInfo } from 'app/store/session/repository';
import { sessionSelector } from 'app/store/session/selectors';
import { LinkingModals } from 'modules/platform-linking/components/LinkingModals';
import { ProviderTile } from 'modules/platform-linking/components/ProviderTile';
import { ProviderTileVariant } from 'modules/platform-linking/components/ProviderTile/provider-tile-variant';
import { getPlatformLinkingProviderList } from 'modules/platform-linking/store/repository';
import { platformLinkingSelector } from 'modules/platform-linking/store/selectors';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { Loader } from 'shared/components/Loader';
import { PAYPAL_CONFIG } from 'config/paypal-config';

const PROVIDER_ID = PlatformLinkingProvider.Paypal;

interface ComponentProps {
  onLinkedAccountChange: (accountFields: { email: string; country: string } | undefined) => void;
}

export const WithdrawAccount: FC<ComponentProps> = ({ onLinkedAccountChange }) => {
  const dispatch = useDispatch();

  const user = useSelector(sessionSelector.activeUser);

  const account = user.connections?.find(
    (connection) => connection.platformId === PROVIDER_ID
  ) as unknown as PaypalMetadataEntity | undefined;

  const actions = useMemo(() => [getPlatformLinkingProviderList()], []);
  const { loading: areConnectionsLoading, fetch: fetchConnections } =
    useFetchData<PlatformLinkingProviderEntity[]>(actions);
  const providers = useSelector(platformLinkingSelector.all);
  const provider = providers.find((p) => p.platformId === PROVIDER_ID);

  const countryMessage = useMemo(() => {
    if (!account?.country) return;
    if (PAYPAL_CONFIG.isInternationalWithdrawEnabled) return;
    if (PAYPAL_CONFIG.withdrawAllowedCountryCodes.includes(account.country)) return;

    return `International withdrawals coming soon. List of allowed countries: ${PAYPAL_CONFIG.withdrawAllowedCountryNames}.`;
  }, [account?.country]);

  const refreshConnectionsHandler = useCallback(() => {
    dispatch(getUserInfo());
    fetchConnections(false);
  }, [dispatch, fetchConnections]);

  useSocketSubscriber([
    {
      action: SocketClientMessage.LinkStatusUpdatedSuccessfully,
      handler: refreshConnectionsHandler,
    },
    {
      action: SocketClientMessage.LinkStatusUpdateFailed,
      handler: refreshConnectionsHandler,
    },
  ]);

  useEffect(() => {
    onLinkedAccountChange(account);
  }, [account, onLinkedAccountChange]);

  if (areConnectionsLoading || !provider) {
    return <Loader />;
  }

  return (
    <Grid container display="flex" alignItems="center" justifyContent="center">
      <Grid item xs={12}>
        <ProviderTile provider={provider} variant={ProviderTileVariant.Outlined} />
      </Grid>
      <Grid item xs={12}>
        <ErrorMessage error={countryMessage}></ErrorMessage>
      </Grid>
      <LinkingModals />
    </Grid>
  );
};
